import type { TransactionDto } from "@home/shared";
import { useState } from "react";
import { api } from "../api.ts";
import { dateTime, money } from "../format.ts";
import { t, tryT, type Lang } from "../i18n.ts";
import { errorText, useMeta } from "../meta.ts";
import { useSync } from "../store.ts";
import { confirmDialog, hapticSelect, hapticTap } from "../telegram.ts";
import { invalidateRemote } from "../useRemote.ts";
import { Sheet } from "./Sheet.tsx";
import { toast } from "./Toast.tsx";
import { txAmount, txTitle } from "./TxList.tsx";

/** Подробности операции; автор или администратор семьи может её удалить или перенести на другой счёт. */
export function TxDetailSheet({ tx, lang, onClose }: { tx: TransactionDto; lang: Lang; onClose: () => void }) {
  const { me } = useSync();
  const meta = useMeta();
  const [busy, setBusy] = useState(false);
  const [moving, setMoving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const amount = txAmount(tx, lang);
  const canDelete = tx.mine || me?.role === "admin";
  // Куда можно перенести: счета в валюте операции, кроме текущего и счёта «куда» у перевода.
  // Чужую операцию администратор переносит только на общий счёт, не на свой личный.
  const targets = (meta.data?.accounts ?? []).filter(
    (a) => a.currency === tx.currency && a.id !== tx.accountId && a.name !== tx.toAccountName && (tx.mine || !a.personal),
  );

  const update = async (body: { accountId?: string; scope?: "personal" | "shared" }, done: string) => {
    hapticSelect();
    setBusy(true);
    setError(null);
    try {
      await api.updateTransaction(tx.id, body);
      hapticTap();
      toast(done);
      invalidateRemote();
      onClose();
    } catch (err) {
      setError(errorText(err));
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!(await confirmDialog(t("confirmDeleteOp"), t("delete")))) return;
    setBusy(true);
    setError(null);
    try {
      await api.deleteTransaction(tx.id);
      hapticTap();
      toast(t("deletedOk"));
      invalidateRemote();
      onClose();
    } catch (err) {
      setError(errorText(err));
      setBusy(false);
    }
  };

  const rows: [string, string][] = [
    [t("category"), tx.type === "transfer" ? `🔁 ${t("transferL")}` : `${tx.icon} ${tx.categoryName ?? ""}`],
    [t("accountL"), tx.type === "transfer" && tx.toAccountName ? `${tx.accountName} → ${tx.toAccountName}` : tx.accountName],
  ];
  // Обмен валюты: сколько пришло на счёт «куда».
  if (tx.toAmount !== null && tx.toCurrency) rows.push([t("receivedL"), money(tx.toAmount, tx.toCurrency, lang)]);
  rows.push(
    [t("date"), dateTime(tx.date)],
    [t("author"), tx.mine ? t("youCap") : tx.authorName],
    [t("source"), tryT(`src.${tx.source}`) ?? tx.source],
  );
  if (tx.personal) rows.push(["🔒", t("personalMark")]);

  return (
    <Sheet title={t("opDetails")} onClose={onClose}>
      <div className="tx-hero">
        <div className={amount.income ? "tx-hero-amount income" : "tx-hero-amount"}>{amount.text}</div>
        {tx.currency !== "UZS" && <div className="hint">≈ {money(tx.amountUzs, "UZS", lang)}</div>}
        <div className="tx-hero-title">{txTitle(tx)}</div>
      </div>
      <dl className="details">
        {rows.map(([k, v]) => (
          <div key={k} className="details-row">
            <dt>{k}</dt>
            <dd>{v}</dd>
          </div>
        ))}
      </dl>
      {error && (
        <p className="form-error" role="alert">
          {error}
        </p>
      )}
      {canDelete && targets.length > 0 && !moving && (
        <button type="button" className="btn btn-block" disabled={busy} onClick={() => setMoving(true)}>
          💳 {t("moveAccount")}
        </button>
      )}
      {moving && (
        <div className="field move-field">
          <span>{t("moveAccount")}</span>
          <div className="chips">
            {targets.map((a) => (
              <button key={a.id} type="button" className="chip" disabled={busy} onClick={() => void update({ accountId: a.id }, t("movedOk", { name: a.name }))}>
                {a.stash ? "🐷" : "💳"} {a.name}
              </button>
            ))}
          </div>
        </div>
      )}
      {/* «Личная / общая» — меняет только автор: чужую личную трату администратор не раскрывает. */}
      {tx.mine && tx.type !== "transfer" && (
        <button
          type="button"
          className="btn btn-block"
          disabled={busy}
          onClick={() => void update({ scope: tx.personal ? "shared" : "personal" }, tx.personal ? t("madeSharedOk") : t("madePersonalOk"))}
        >
          {tx.personal ? t("makeSharedOp") : t("makePersonalOp")}
        </button>
      )}
      {canDelete && (
        <button type="button" className="btn danger btn-block" disabled={busy} onClick={() => void remove()}>
          🗑 {t("deleteOp")}
        </button>
      )}
    </Sheet>
  );
}
